import { styled } from "@mui/system";
import { Box } from "@mui/material";

export const NewIngredientContainer = styled(Box)(({ theme }) => ({
  "&.newIngredientRoot": {
    padding: theme.spacing(3, 2, 2, 2),
    backgroundColor: theme.palette.grey.light,
    minHeight: "100vh",
    width: "100%",
    margin: "5 auto",
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(3, 2, 2, 2),
    },
    maxWidth: "100%",
  },

  /* ---------- Left column: header + scrollable body + sticky footer ---------- */
  "& .formColumn": {
    height: theme.pageHeight,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(0, 1, 0),
    [theme.breakpoints.down("md")]: {
      height: "auto",
      padding: theme.spacing(0, 1, 0),
      overflow: "visible",
    },
  },
  "& .formHeader": {
    flexShrink: 0,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.75),
  },
  "& .formBody": {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingBottom: theme.spacing(1.5),
    paddingRight: theme.spacing(0.5),
    /* Thin custom scrollbar */
    "&::-webkit-scrollbar": { width: "5px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.grey[300],
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.grey[400],
    },
    [theme.breakpoints.down("md")]: {
      overflow: "visible",
      paddingRight: 0,
      paddingBottom: theme.spacing(1),
    },
  },
  "& .formBody .MuiGrid-item": {
    paddingTop: "6px",
    paddingBottom: "6px",
  },

  /* ---------- Section cards ---------- */
  "& .formSection": {
    backgroundColor: theme.palette.white.main,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5),
    border: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 3px 12px rgba(0,0,0,0.07)",
    },
  },
  "& .formSection.compactSection": {
    padding: theme.spacing(2, 2.5),
  },

  /* ---------- Colored accent bar before section titles ---------- */
  "& .sectionTitle": {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text?.primary || "#1a1a2e",
    marginBottom: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "&::before": {
      content: '""',
      display: "inline-block",
      width: "4px",
      height: "18px",
      backgroundColor: theme.palette.primary.main,
      borderRadius: "2px",
      flexShrink: 0,
    },
  },

  "& .categoriesSection": {
    display: "flex",
    flexDirection: "column",
    minHeight: theme.spacing(22),
  },
  "& .chipList": {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    paddingTop: theme.spacing(0.75),
  },
  "& .dateTimePickerComponent": {
    width: "100%",
    "& input": {
      height: theme.spacing(1),
      fontSize: theme.typography.pxToRem(14),
    },
  },
  "& .dateTimeRangeBottom": {
    display: "flex",
    alignItems: "end",
  },
  "& .submissionButton": {
    minWidth: theme.spacing(14),
    fontWeight: 600,
    textTransform: "none",
    borderRadius: theme.spacing(1),
    fontSize: theme.typography.pxToRem(14),
  },
  "& .filterBox": {
    display: "flex",
    textAlign: "left",
    paddingTop: theme.spacing(2),
  },
  "& .submissionBox": {
    display: "flex",
    justifyContent: "flex-end",
    textAlign: "right",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    position: "sticky",
    bottom: 0,
    background: theme.palette.white.main,
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    gap: theme.spacing(1),
  },

  /* ---------- Sticky footer buttons ---------- */
  "& .pageFooterActions": {
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1),
    backgroundColor: theme.palette.grey.light,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },

  "& .infoDuration": {
    margin: theme.spacing(1),
    color: theme.palette.grey[600],
  },
  "& .endError": {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(13),
    marginLeft: theme.spacing(1),
  },
  "& .emptyListText": {
    mb: 1,
    textAlign: "center",
    opacity: 0.5,
    padding: theme.spacing(0.5),
  },
  "& .createExamIconBox": {
    backgroundColor: theme.palette.yellow.main,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.5),
    width: theme.spacing(5.5),
    height: theme.spacing(5.5),
    marginRight: theme.spacing(1),
  },
  "& .regionsSideBarIconBox": {
    backgroundColor: theme.palette.primary.main,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: theme.spacing(0.75),
    width: theme.spacing(3.5),
    height: theme.spacing(3.5),
    marginRight: theme.spacing(1),
  },
  "& .icon": {
    color: theme.palette.white.main,
  },

  /* ---------- Right sidebar ---------- */
  "& .regionsSideBar": {
    position: "sticky",
    top: theme.spacing(1),
    alignSelf: "flex-start",
    width: "100%",
    height: `calc(${theme.pageHeight} - 16px)`,
    display: "flex",
    flexDirection: "column",
    maxWidth: "100%",
    [theme.breakpoints.down("md")]: {
      position: "relative",
      top: "auto",
      marginLeft: 0,
      marginTop: theme.spacing(2),
      height: "auto",
    },
  },
  "& .regionsSideBarHeader": {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  "& .categoriesList": {
    border: "1px solid",
    borderColor: theme.palette.grey.main,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    maxHeight: `calc(${theme.pageHeight} - 520px)`,
    overflow: "auto",
    marginBottom: theme.spacing(2),
    "@media (min-height: 800px)": {
      maxHeight: "50vh",
    },
  },
  "& .regionsPanel": {
    backgroundColor: theme.palette.white.main,
    flex: 1,
    overflow: "auto",
    marginBottom: theme.spacing(1),
  },
  "& .disabledItem": {
    opacity: 0.6,
  },
  "& .tabsHeaderWrapper": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  "& .overrideSelectedTab": {
    backgroundColor: theme.palette.white.main,
  },
  "& .overrideTabsIndicator": {
    height: 0,
  },
  "& .overrideTabPanelRoot": {
    backgroundColor: theme.palette.white.main,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    /* Thin custom scrollbar */
    "&::-webkit-scrollbar": { width: "5px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.grey[300],
      borderRadius: "4px",
    },
  },
}));
